import { useFormikContext } from 'formik';
import React, { useState } from 'react';
import { Platform, TextInputProps, Modal } from 'react-native';
import styled from 'styled-components/native';

import Colors from '../../constants/Colors';
import { FormInputVariant } from './FormInput';
import FormSelect from './FormSelect';

import DateTimePicker from '@react-native-community/datetimepicker';
import FlatListPicker from '../../components/FlatlistPicker';
import { Subtitle, B } from '../Typography';

import { format } from 'date-fns';
import { log } from '../../tools/LogHelper';
import elementIDs from '../../../elementIDs';

const FormInputView = styled.View`
  width: 100%;
  height: 53px;
`;

const LabelText = styled.Text<{ error?: boolean }>`
  font-family: 'DMSans-Bold';
  font-size: 16px;
  line-height: 20px;
  color: ${p => (p.error ? Colors.error : Colors.secondaryText)};
  margin-bottom: 2px;
`;

const StyledPickerView = styled.View`
  background-color: white;
  width: 100%;
  padding-top: 10px;
  padding-bottom: 10px;
  max-width: ${Platform.OS === 'ios' ? 350 : 280}px;
  border-radius: 8px;
`;

const StyledIOSModalButton = styled.TouchableOpacity`
  background-color: white;
  width: 100%;
  padding-top: 10px;
  align-items: center;
  justify-content: center;
  margin-top: 8px;
  padding-bottom: 10px;
  max-width: ${Platform.OS === 'ios' ? 350 : 280}px;
  border-radius: 8px;
`;

const StyledModalView = styled.View`
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
`;

const StyledModalBackgroundContainer = styled.TouchableWithoutFeedback`
  width: 100%;
  height: 100%;
  position: absolute;
`;

const StyledModalBackground = styled.View`
  width: 100%;
  height: 100%;
  position: absolute;
  background-color: black;
  opacity: 0.3;
`;

const StyledTimePickerView = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const StyledModalButtonView = styled.View`
  flex-direction: row;
  width: 100%;
  align-items: center;
  justify-content: flex-end;

  margin-top: 32px;
  padding-bottom: 4px;
`;

const StyledModalButton = styled.TouchableOpacity`
  height: 20px;
  margin-right: 16px;
`;

const StyledTimeInputView = styled.TouchableOpacity<{
  hasHelperText: boolean;
  error?: boolean;
}>`
  min-height: auto;
  background-color: #fff;
  border: 1px solid ${p => (p.error ? Colors.error : Colors.formInputBorder)};
  border-radius: 8px;
  justify-content: center;
  padding: 8px 12px 0px 12px;
  margin-right: 10px;
  width: 100%;
  height: 53px;
  margin-bottom: ${p => (p.error || p.hasHelperText ? 12 : 12)}px;
`;

const StyledInputText = styled(B)<{ hasValue?: boolean }>`
  font-family: ${p => (p.hasValue ? 'DMSans-Bold' : 'WorkSans-Regular')};
  font-size: 16px;
  line-height: 18px;
  opacity: ${p => (p.hasValue ? 1 : 0.3)};
`;

const HelperText = styled.Text`
  font-family: 'DMSans-Medium';
  font-size: 12px;
  line-height: 18px;
  color: ${Colors.formLabel};
  margin: 4px 4px 12px 4px;
`;

const ErrorText = styled(HelperText)`
  color: ${Colors.error};
`;

export type FormInputProps = TextInputProps & {
  name: string;
  label?: string;
  helperText?: string;
  variant?: FormInputVariant;
};

const StyledContainer = styled.View`
  flex-direction: row;
  flex: 1;
`;

const StyledFormSelectContainer = styled.View<{ marginRight?: number }>`
  margin-right: ${p => (p.marginRight ? p.marginRight : 0)}px;
  flex: 1;
`;

const FormTimeInput: React.FC<FormInputProps> = ({
  name,
  label,
  helperText,
  variant,
  ...props
}) => {
  const { touched, errors, values, setFieldValue } = useFormikContext<any>();

  const buildWebOptions = () => {
    const arr: string[] = [];
    for (let i = 1; i <= 12; i++) {
      for (let x = 0; x < 60; x += 15) {
        let hour: any = i;
        if (hour < 10) {
          hour = '0' + i;
        }
        if (x === 0) {
          arr.push(hour + ':' + '00');
        } else {
          arr.push(hour + ':' + x);
        }
      }
    }
    return arr;
  };

  const error = touched[name] && errors[name];
  const [showPicker, setShowPicker] = useState(false);
  const [time, setTime] = useState({ hour: '01', min: '00', timeOfDay: 'AM' });
  const today = new Date();
  const [date, setDate] = useState(
    new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      Number(time.hour) + Number(time.timeOfDay === 'PM' ? 12 : 0),
      Number(time.min)
    )
  );
  return (
    <FormInputView>
      {label && <LabelText error={Boolean(error)}>{label}</LabelText>}
      <StyledContainer testID={elementIDs.timeSelectorID}>
        {Platform.OS !== 'web' ? (
          <StyledTimeInputView
            hasHelperText={Boolean(helperText)}
            error={Boolean(error)}
            onPress={() => {
              setShowPicker(true);
            }}
          >
            <StyledInputText color='dark' hasValue={Boolean(values[name])}>
              {values[name]
                ? values[name] + ' ' + values['timeOfDay']
                : 'HH:MM aa'}
            </StyledInputText>
          </StyledTimeInputView>
        ) : (
          <StyledContainer style={{ height: 53 }}>
            <StyledFormSelectContainer marginRight={8}>
              <FormSelect
                name={name}
                placeholder='HH:MM'
                options={buildWebOptions()}
              />
            </StyledFormSelectContainer>
            <StyledFormSelectContainer>
              <FormSelect
                name='timeOfDay'
                placeholder='AM/PM'
                options={['AM', 'PM']}
              />
            </StyledFormSelectContainer>
          </StyledContainer>
        )}
      </StyledContainer>
      {error ? (
        <ErrorText>{error}</ErrorText>
      ) : (
        helperText && <HelperText>{helperText}</HelperText>
      )}
      <Modal
        visible={showPicker && Platform.OS === 'android'}
        transparent={true}
        animationType='fade'
      >
        <StyledModalView>
          <StyledModalBackgroundContainer
            onPress={() => {
              setShowPicker(false);
            }}
          >
            <StyledModalBackground />
          </StyledModalBackgroundContainer>
          <StyledPickerView>
            <StyledTimePickerView>
              <FlatListPicker
                onValueChanged={data => {
                  setTime({ ...time, hour: data });
                  log(data);
                }}
                initialValue={time.hour}
                options={[
                  '01',
                  '02',
                  '03',
                  '04',
                  '05',
                  '06',
                  '07',
                  '08',
                  '09',
                  '10',
                  '11',
                  '12'
                ]}
              />
              <Subtitle color='dark'>:</Subtitle>
              <FlatListPicker
                onValueChanged={data => {
                  setTime({ ...time, min: data });
                }}
                initialValue={time.min}
                options={['00', '15', '30', '45']}
              />
              <FlatListPicker
                onValueChanged={data => {
                  setTime({ ...time, timeOfDay: data });
                }}
                initialValue={values['timeOfDay'] || time.timeOfDay}
                options={['AM', 'PM']}
              />
            </StyledTimePickerView>
            <StyledModalButtonView>
              <StyledModalButton
                onPress={() => {
                  setShowPicker(false);
                }}
              >
                <B color='dark'>Cancel</B>
              </StyledModalButton>
              <StyledModalButton
                onPress={() => {
                  setFieldValue(name, time.hour + ':' + time.min);
                  setFieldValue('timeOfDay', time.timeOfDay);
                  setShowPicker(false);
                }}
              >
                <B testID={elementIDs.confirmButtonID}>Confirm</B>
              </StyledModalButton>
            </StyledModalButtonView>
          </StyledPickerView>
        </StyledModalView>
      </Modal>
      <Modal
        visible={showPicker && Platform.OS === 'ios'}
        transparent={true}
        animationType='fade'
      >
        <StyledModalView>
          <StyledModalBackgroundContainer
            onPress={() => {
              setShowPicker(false);
            }}
          >
            <StyledModalBackground />
          </StyledModalBackgroundContainer>
          <StyledPickerView>
            <DateTimePicker
              themeVariant='light'
              value={date}
              display='spinner'
              mode='time'
              onChange={(_, date) => {
                setDate(new Date(date));
                setTime({
                  hour: format(new Date(date), 'h'),
                  timeOfDay: format(new Date(date), 'aa'),
                  min: format(new Date(date), 'mm')
                });
              }}
              minuteInterval={15}
              is24Hour={false}
            />
          </StyledPickerView>
          <StyledIOSModalButton
            onPress={() => {
              setShowPicker(false);
              setFieldValue(
                name,
                (time.hour.length > 1 ? time.hour : '0' + time.hour) +
                  ':' +
                  time.min
              );
              setFieldValue('timeOfDay', time.timeOfDay);
            }}
          >
            <B testID={elementIDs.confirmButtonID}>Confirm</B>
          </StyledIOSModalButton>
          <StyledIOSModalButton
            onPress={() => {
              setShowPicker(false);
            }}
          >
            <B color='dark'>Cancel</B>
          </StyledIOSModalButton>
        </StyledModalView>
      </Modal>
    </FormInputView>
  );
};

export default FormTimeInput;
